import {
    ReadonlyMat3,
    ReadonlyMat4,
    ReadonlyVec2,
    ReadonlyVec3,
    vec2,
    vec3,
} from 'gl-matrix';

export function VerticesToArrays(vertices: Array<Vertex>): Array<Float32Array> {
    const first = vertices[0];
    const buffers = [
        new Float32Array(vertices.length * 2),
        ...first.map(
            (component) =>
                new Float32Array(vertices.length * component.dimensions)
        ),
    ];
    let index = 0;
    for (const v of vertices) {
        buffers[0].set([v.coordinates[0], v.coordinates[1]], index * 2);
        let j = 1;
        for (const c of v.components) {
            buffers[j++].set(c.data, index * c.dimensions);
        }
        ++index;
    }
    return buffers;
}

/**
 * {@link Vertex} represents a single point in 3D space.\
 * It has {@link coordinates PatientSpace coordinates} as well as an arbitrary number of {@link VertexData} {@link Vertex.components components}.
 */
export class Vertex {
    /**
     * PatientSpace coordinates of the {@link Vertex}.
     */
    private _coordinates: vec3;

    /**
     * {@link VertexData} components stored within this {@link Vertex}.
     */
    private _components: VertexData<vec2 | vec3>[];

    /**
     * Method to construct {@link Vertex} from {@link vertexCoordinates PatientSpace coordinates} and {@link components VertexComponents}.
     * @param vertexCoordinates The PatientSpace coordinates of the {@link Vertex}.
     * @param components The {@link VertexData components} stored within this {@link Vertex}.
     */
    public constructor(
        vertexCoordinates: ReadonlyVec3 | Vertex,
        ...components: (ReadonlyVec3 | ReadonlyVec2)[]
    ) {
        if (vertexCoordinates instanceof Vertex) {
            this._coordinates = vec3.clone(vertexCoordinates._coordinates);
            this._components = new Array<VertexData<vec2 | vec3>>(
                vertexCoordinates.components.length
            );
            let i = 0;
            for (const data of vertexCoordinates.components) {
                this._components[i++] =
                    data.data.length === 3
                        ? new VertexData<vec3>(vec3.clone(data.data))
                        : new VertexData<vec2>(
                              vec2.clone(data.data as ReadonlyVec2)
                          );
            }
        } else {
            this._coordinates = vec3.clone(vertexCoordinates);
            this._components = new Array<VertexData<vec2 | vec3>>(
                components.length
            );
            let i = 0;
            for (const data of components) {
                this._components[i++] =
                    data.length === 3
                        ? new VertexData<vec3>(vec3.clone(data))
                        : new VertexData<vec2>(
                              vec2.clone(data as ReadonlyVec2)
                          );
            }
        }
    }

    /**
     * Method to update the {@link vertexCoordinates} and {@link vertexData} of the {@link Vertex}.
     * @throws if the number of elements in {@link vertexData} does not match the number of {@link components} in this vector.\
     * Also throws if the types do not match.
     * @param vertexCoordinates The new PatientSpace coordinates of the {@link Vertex}.
     * @param vertexData The new vertexData to be stored within the {@link components Vertex components}.\
     * The provided {@link vertexData} must be of same size and type as the existing {@link components Vertex components}.
     */
    public set(
        vertexCoordinates: ReadonlyVec3,
        ...vertexData: (ReadonlyVec3 | ReadonlyVec2)[]
    ): Vertex {
        vec3.copy(this._coordinates, vertexCoordinates);
        let i = 0;
        if (vertexData.length !== this._components.length) {
            throw new Error(`Attempt to update vertex data of vertex with ${this._components.length} components.\
            Number of components provided in the method call was ${vertexData.length}`);
        }
        for (const data of vertexData) {
            this._components[i++].update(data);
        }
        return this;
    }

    /**
     * Method to get reference to the {@link VertexData} stored at the specified index.
     * @param index Index of the {@link VertexData} to be retrieved.
     * @returns Reference to the {@link VertexData} stored at the specified index. Undefined if the index is out of bounds.
     */
    public get(index: number): VertexData<vec2 | vec3> | undefined {
        if (index >= this._components.length) {
            return undefined;
        }
        return this._components[index];
    }

    public map(
        fn: (value: VertexData<vec2 | vec3>) => unknown
    ): Float32Array[] {
        return this._components.map(fn) as Float32Array[];
    }

    /**
     * Static method to linearly interpolate two (2)  {@link Vertex vertices}.\
     * This method creates a new {@link Vertex} that is the linear interpolation between the two provided as parameters.\
     * It interpolates the vertex coordinates and all the other {@link components}.
     * @param a The first {@link Vertex} to interpolate.
     * @param b The second {@link Vertex} that is to be interpolated with.
     * @param t The multiplier between 0 and 1.
     * @returns
     */
    public static lerp(a: Vertex, b: Vertex, t: number): Vertex {
        if (a.components.length !== b.components.length) {
            throw new Error(
                `Vertex linear interpolation between vertices with different number of components ${a.components.length} vs ${b.components.length}`
            );
        }
        const vertexCoordinate = vec3.lerp(
            vec3.create(),
            a._coordinates,
            b._coordinates,
            t
        );
        const components = new Array<ReadonlyVec2 | ReadonlyVec3>(
            a.components.length
        );
        for (let i = 0; i < components.length; ++i) {
            components[i] = VertexData.lerp(
                a.components[i],
                b.components[i],
                t
            );
        }
        return new Vertex(vertexCoordinate, ...components);
    }

    /**
     * Method to get the {@link _coordinates PatientSpace coordinates} of this {@link Vertex}.
     */
    public get coordinates(): ReadonlyVec3 {
        return this._coordinates;
    }

    /**
     * Method to transform the Vertex from PatientSpace to some different Space.\
     * This only updates the vertex coordinates
     * @param transformation The 3x3 or 4x4 transformation to be applied to this {@link Vertex}.
     */
    public transform(transformation: ReadonlyMat3 | ReadonlyMat4): Vertex {
        if (transformation.length === 9) {
            vec3.transformMat3(
                this._coordinates,
                this._coordinates,
                transformation
            );
        } else {
            vec3.transformMat4(
                this._coordinates,
                this._coordinates,
                transformation as ReadonlyMat4
            );
        }
        return this;
    }

    public get components(): ReadonlyArray<VertexData<vec2 | vec3>> {
        return this._components;
    }
}

export class VertexData<Dimensions extends vec2 | vec3> {
    public data: Dimensions;

    public constructor(data: Dimensions) {
        this.data = data;
    }

    public update(data: ReadonlyVec2 | ReadonlyVec3): VertexData<Dimensions> {
        if (this.data.length === 3 && data.length === 3) {
            vec3.copy(this.data, data);
        } else if (this.data.length === 2 && data.length === 2) {
            vec2.copy(this.data, data);
        } else {
            throw new Error(
                `Attempt to store data of type ${data.constructor.name} in VertexData object with content type ${this.data.constructor.name}`
            );
        }
        return this;
    }

    public static lerp(
        a: VertexData<vec2 | vec3>,
        b: VertexData<vec2 | vec3>,
        t: number
    ): ReadonlyVec2 | ReadonlyVec3 {
        if (a.data.length === 3 && b.data.length === 3) {
            return vec3.lerp(vec3.create(), a.data, b.data, t);
        } else if (a.data.length === 2 && b.data.length === 2) {
            return vec2.lerp(vec2.create(), a.data, b.data, t);
        } else {
            throw new Error(
                `Attempt to linearly interpolate VertexData of objects with different data types.`
            );
        }
    }

    public get dimensions(): number {
        return this.data.length;
    }
}
